/**
 * <ShareMoment /> — milestone celebration + word-of-mouth share.
 *
 * Triggers ONCE when the user completes Day 7, 14, or 21 of a journey.
 * Renders the single gold halo permitted outside completed-day badges
 * (per design-system rule: "gold for milestones only"), a prefilled soft
 * message, and a share sheet with the platform's Web Share API or fallback
 * copy/native links.
 *
 * Storage key: `reclaim_milestone_seen_v1_<slug>_<n>` = "true" once seen.
 *
 * Inserted as an optional step between <CompletionSeal> and "night" on
 * milestone days only. On non-milestone days it's a no-op.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Share2, Check } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";

const MILESTONE_DAYS = [7, 14, 21];

const MESSAGES = {
  7: "Seven days in. I'm noticing it's working — quiet but real.",
  14: "Two weeks of this. Something has genuinely shifted.",
  21: "Twenty-one days. The road feels different now.",
};

const TITLES = {
  7: "One week in.",
  14: "Two weeks in.",
  21: "You arrived.",
};

const SUB = {
  7: "Quietly noticing the shift.",
  14: "Halfway. The path is widening.",
  21: "21 days. Felt, not forced.",
};

export function isMilestoneDay(day) {
  return MILESTONE_DAYS.includes(Number(day));
}

export function hasSeenMilestone(slug, day) {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(`reclaim_milestone_seen_v1_${slug}_${day}`) === "true";
}

function markSeen(slug, day) {
  try {
    localStorage.setItem(`reclaim_milestone_seen_v1_${slug}_${day}`, "true");
  } catch {}
}

export default function ShareMoment({ slug, day, onContinue }) {
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState(null);

  const message = useMemo(() => MESSAGES[day] || MESSAGES[21], [day]);
  const title = useMemo(() => TITLES[day] || "A milestone.", [day]);
  const sub = useMemo(() => SUB[day] || "", [day]);

  // Mark seen the moment the screen mounts so a refresh doesn't re-trigger.
  useEffect(() => {
    if (slug && day) markSeen(slug, day);
  }, [slug, day]);

  const tryNativeShare = async () => {
    setShareError(null);
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "Reclaim",
          text: message,
        });
        return;
      } catch {
        // user cancelled or platform blocked — fall through to copy
      }
    }
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      setShareError("Couldn't open share — try long-pressing the message to copy.");
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      setShareError("Couldn't copy — try long-pressing the message.");
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center px-6 pb-10 pt-16"
      style={{ background: P.bgWash }}
      data-testid="share-moment"
    >
      {/* Gold halo — milestone moment, design-system permitted */}
      <motion.div
        className="absolute"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.78 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          top: "26%",
          width: 260,
          height: 260,
          borderRadius: 999,
          background:
            "radial-gradient(circle, rgba(212,184,126,0.28) 0%, rgba(212,184,126,0.06) 55%, rgba(212,184,126,0) 80%)",
          filter: "blur(2px)",
        }}
        aria-hidden
      />
      <motion.div
        className="absolute"
        animate={{ scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          top: "26%",
          width: 84,
          height: 84,
          marginTop: 88,
          borderRadius: 999,
          background:
            "radial-gradient(circle at 50% 45%, rgba(255,250,238,0.95) 0%, rgba(212,184,126,0.55) 55%, rgba(212,184,126,0) 100%)",
          boxShadow: "0 0 60px rgba(212,184,126,0.45)",
        }}
        aria-hidden
      />

      <div className="relative w-full max-w-md mx-auto flex flex-col items-center text-center">
        <p
          className="text-[10px] uppercase tracking-[0.42em] mb-2"
          style={{ color: P.accentGold, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          data-testid="share-moment-label"
        >
          Day {day} · Milestone
        </p>
        <h2
          className="italic mb-3"
          style={{
            color: "#FFFFFF",
            fontFamily: JOURNEY_FONT_HEAD,
            fontWeight: 300,
            fontSize: "clamp(28px, 5.4vw, 38px)",
            letterSpacing: "-0.01em",
            textShadow: "0 4px 28px rgba(212,184,126,0.32)",
          }}
        >
          {title}
        </h2>
        <p
          className="mb-12"
          style={{
            color: "rgba(255,255,255,0.78)",
            fontFamily: JOURNEY_FONT_HEAD,
            fontStyle: "italic",
            fontWeight: 300,
            fontSize: 15,
          }}
        >
          {sub}
        </p>

        {/* Pre-filled message preview */}
        <div
          className="w-full rounded-2xl p-5 mb-5 relative"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(212,184,126,0.20)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.36em] mb-2"
            style={{ color: P.accentGold, fontFamily: JOURNEY_FONT_LABEL }}
          >
            A note you can share
          </p>
          <p
            className="text-[15.5px] leading-[1.45]"
            style={{
              color: "rgba(255,255,255,0.92)",
              fontFamily: JOURNEY_FONT_HEAD,
              fontStyle: "italic",
              fontWeight: 400,
            }}
            data-testid="share-moment-message"
          >
            "{message}"
          </p>
        </div>

        {/* Share + copy buttons */}
        <div className="grid grid-cols-2 gap-3 w-full mb-5">
          <motion.button
            onClick={tryNativeShare}
            whileTap={{ scale: 0.985 }}
            whileHover={{ scale: 1.012 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="rounded-full py-3 flex items-center justify-center gap-2"
            style={{
              background: `linear-gradient(135deg, ${P.accentGold} 0%, #8E7349 100%)`,
              color: "#13212F",
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 600,
              fontSize: 13.5,
              border: "1px solid rgba(212,184,126,0.6)",
              boxShadow: "0 12px 30px rgba(168,137,92,0.30)",
            }}
            data-testid="share-moment-share-btn"
          >
            <Share2 size={14} strokeWidth={2.4} />
            Share
          </motion.button>
          <motion.button
            onClick={copyToClipboard}
            whileTap={{ scale: 0.985 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="rounded-full py-3 flex items-center justify-center gap-2"
            style={{
              background: "rgba(255,255,255,0.04)",
              color: copied ? P.accentGold : "rgba(255,255,255,0.85)",
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 500,
              fontSize: 13.5,
              border: "1px solid rgba(255,255,255,0.10)",
            }}
            data-testid="share-moment-copy-btn"
          >
            {copied ? <Check size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2.2} />}
            {copied ? "Copied" : "Copy"}
          </motion.button>
        </div>

        <AnimatePresence>
          {shareError && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[12px] mb-4"
              style={{
                color: "rgba(255,255,255,0.55)",
                fontFamily: JOURNEY_FONT_BODY,
              }}
            >
              {shareError}
            </motion.p>
          )}
        </AnimatePresence>

        <button
          onClick={onContinue}
          className="text-[11px] tracking-[0.32em] uppercase opacity-65 hover:opacity-100 transition-opacity"
          style={{ color: "rgba(255,255,255,0.78)", fontFamily: JOURNEY_FONT_LABEL }}
          data-testid="share-moment-skip-btn"
        >
          continue →
        </button>
      </div>
    </div>
  );
}
